import React, { FC, useState, useMemo } from 'react'
import s from './Bookings.module.scss'
import ProfileMenuHeader from '../ProfileMenuHeader'
import { Menu } from '../ProfileMenu'
import useIsAuth from 'sdk/hooks/useIsAuth'
import { ReactComponent as RiyalIcon } from 'images/new-icons/riyal.svg'
import Page from '../../Page'
import useGetMyBookings from 'sdk/hooks/shop/useGetBookings'
import { format, parseISO, isFuture, isPast } from 'date-fns'
import Button from 'components/shared/Button'
import BookingDetails from 'modules/bookings/BookingDetails'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

type Props = {
  onClose: any
  onRefund: any
}


const Bookings: FC<Props> = ({ onClose, onRefund }) => {
  const {t} = useTranslation() as any
  const [activeFilter, setActiveFilter] = useState<'scheduled' | 'past'>('scheduled')
  const [selectedBooking, setSelectedBooking] = useState(null)
  const { data: isAuthData } = useIsAuth()
  const userId = isAuthData?.user?.id
  const { data: myBookings } = useGetMyBookings(userId)
  const navigate = useNavigate()

  const handleClose = () => {
    onClose(Menu.PROFILE)
  }

  const handleBookingClick = (booking) => {
    navigate(`/booking/${booking?.id}`)
  }

  const handleCloseDetails = () => {
    setSelectedBooking(null)
  }

  const handleRefundClick = (booking) => {
    onRefund(booking)
  }

  const handleLeaveReviewClick = (booking) => {
    navigate(`/salon/${booking?.salon_id}/add-review?serviceId=${booking?.service}&specialistId=${booking?.team_id}`)
  }

  const filteredBookings = useMemo(() => {
    if (!myBookings) return []

    return myBookings.filter(booking => {
      const bookingDate = parseISO(`${booking.date}T${booking.start_time}`)

      if (activeFilter === 'scheduled') {
        return isFuture(bookingDate)
      } else {
        return isPast(bookingDate)
      }
    }).sort((a, b) => {
      const dateA = parseISO(`${a.date}T${a.start_time}`)
      const dateB = parseISO(`${b.date}T${b.start_time}`)
      return dateB.getTime() - dateA.getTime()
    })
  }, [myBookings, activeFilter])

  if (selectedBooking) {
    return (
      <BookingDetails
        booking={selectedBooking}
        onClose={handleCloseDetails}
      />
    )
  }

  return (
    <div style={{ overflow: 'auto' }} className={s.purchases_container}>
      <Page activeLink='/profile' disableOverflow={true}>
        <ProfileMenuHeader
          text="My Bookings"
          onClose={handleClose}
        />

        <div >
          <div className={s.purchases_filter}>
            <Button
              yellow={activeFilter === 'scheduled'}
              white={activeFilter !== 'scheduled'}
              onClick={() => setActiveFilter('scheduled')}
            >
              {t('Scheduled')}
            </Button>
            <Button
              yellow={activeFilter === 'past'}
              white={activeFilter !== 'past'}
              onClick={() => setActiveFilter('past')}
            >
              {t("Past")}
            </Button>
          </div>

          {filteredBookings.map((item) => {
            return (
              <div
                className={s.purchases_card}
                key={item.id}


              >
                <div onClick={() => handleBookingClick(item)} className={s.purchases_card_image_wrapper}>
                  <img className={s.purchases_card_image} src={item?.team?.avatar} alt="Product Image" />

                  <div className={s.purchases_card_info}>
                    <div className={s.purchases_card_info_date_time}>
                      <div className={s.purchases_card_info_date}>
                        {item?.date ? format(parseISO(item.date), 'MMM d, yyyy') : ''}
                      </div>
                      <div className={s.purchases_card_info_time}>
                        {item?.start_time ? format(parseISO(`2000-01-01T${item.start_time}`), 'h:mm a') : ''}
                      </div>
                    </div>

                    <div className={s.purchases_card_info_title}>{item?.team?.full_name}</div>
                    <div className={s.purchases_card_info_subtitle}>{item?.teams?.description || 'No description'}</div>
                    <div className={s.purchases_card_info_footer}>
                      <div className={s.purchases_card_info_footer_price}>
                        <RiyalIcon className={s.riyal_icon} />
                        {item?.price}
                      </div>
                      {item?.status === 'Completed' && (
                        <Button
                          bold
                          gray
                          className={s.purchases_card_info_footer_button}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeaveReviewClick(item);
                          }}
                        >
                          {t('Leave your review')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  bold
                  wide
                  gray
                  className={s.purchases_card_cancel_button}
                  onClick={() => handleRefundClick(item)}
                >
                  {t('Get A Refund')}
                </Button>
              </div>
            )
          })}
        </div>
      </Page>
    </div>
  )
}

export default Bookings